import React, { useEffect, useState, useMemo } from "react";
import { fromLocalISOString, toLocalISOString } from '@/utils/timezoneUtils';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useShiftTemplates } from "../hooks/useShiftTemplates";
import { CalendarEvent } from "../types";
import { format, parseISO, addDays, startOfWeek, endOfWeek, differenceInDays, setHours, setMinutes, setSeconds, isSameDay, getHours, getMinutes, getSeconds, addMinutes, isWithinInterval } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { Trash2, Calendar, ChevronRight, Save, PlusCircle } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import SaveTemplateDialog from "./SaveTemplateDialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface TemplatesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  onApplyTemplate: (shifts: Omit<CalendarEvent, 'assigned_guards' | 'guard_names'>[]) => void;
  currentEvents?: CalendarEvent[];
  currentWeekStart?: Date;
}

const TemplatesDialog: React.FC<TemplatesDialogProps> = ({
  isOpen,
  onClose,
  teamId,
  onApplyTemplate,
  currentEvents = [],
  currentWeekStart
}) => {
  const { t } = useLanguage();
  const { templates, isLoading, error, fetchTemplates, deleteTemplate } = useShiftTemplates(teamId);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [appliedTemplateIds, setAppliedTemplateIds] = useState<Set<string>>(new Set());

  // Filter current events to only include the current week and current team when saving as template
  const currentWeekEvents = useMemo(() => {
    if (!currentWeekStart || !currentEvents.length) {
      return currentEvents;
    }

    const weekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 1 });
    
    return currentEvents.filter(event => {
      const eventStart = fromLocalISOString(event.start_date);
      const isInCurrentWeek = isWithinInterval(eventStart, {
        start: currentWeekStart,
        end: weekEnd
      });
      
      // Only include shifts that belong to the current team
      const belongsToCurrentTeam = event.team_id === teamId;
      
      return isInCurrentWeek && belongsToCurrentTeam;
    });
  }, [currentEvents, currentWeekStart, teamId]);

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
      // Reset applied templates when dialog opens
      setAppliedTemplateIds(new Set());
    }
  }, [isOpen, teamId]);

  const handleApplyTemplate = async (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (!template || !template.schedule_data || !template.schedule_data.shifts) {
      toast.error('Invalid template data');
      return;
    }

    // Check if this template has already been applied in this session
    if (appliedTemplateIds.has(templateId)) {
      toast.info('Template already applied in this session');
      return;
    }

    // CRITICAL: Ensure currentWeekStart is provided
    if (!currentWeekStart) {
      console.error('CRITICAL ERROR: No target week specified for template application');
      toast.error('Cannot apply template: No target week specified. Please ensure you are in week view mode.');
      return;
    }

    // Prevent duplicate execution with more robust check
    if (isApplying) {
      console.log('Template application already in progress, skipping...');
      return;
    }

    setIsApplying(true);
    try {
      // Get current user
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id;
      
      if (!userId) {
        throw new Error('User not authenticated');
      }

      console.log('Applying template to EXACT target week:', format(currentWeekStart, 'yyyy-MM-dd (EEE)'));
      
      // Process template shifts and map them to the target week
      const shiftsToCreate = template.schedule_data.shifts.map(shift => {
        const originalStartDate = fromLocalISOString(shift.start_date);
        const originalEndDate = fromLocalISOString(shift.end_date);
        
        console.log('Processing original shift:', {
          start: format(originalStartDate, 'EEE dd/MM HH:mm'),
          end: format(originalEndDate, 'EEE dd/MM HH:mm'),
          title: shift.title
        });
        
        // Get the original week start from the template
        const originalWeekStart = startOfWeek(originalStartDate, { weekStartsOn: 1 });
        
        // Calculate the day offset from the original week start
        const startDayOffset = differenceInDays(originalStartDate, originalWeekStart);
        
        // Create new start date in the TARGET week using the same day offset
        const newStartDate = addDays(currentWeekStart, startDayOffset);
        
        // Preserve the exact time from the original shift
        const finalStartDate = setSeconds(
          setMinutes(
            setHours(newStartDate, getHours(originalStartDate)), 
            getMinutes(originalStartDate)
          ), 
          getSeconds(originalStartDate)
        );
        
        // Calculate the duration in minutes from the original shift
        const originalDurationMinutes = Math.abs(originalEndDate.getTime() - originalStartDate.getTime()) / (1000 * 60);
        
        // Apply the EXACT same duration to get the end date
        const finalEndDate = addMinutes(finalStartDate, originalDurationMinutes);
        
        console.log('Applied shift result:', {
          originalStart: format(originalStartDate, 'EEE dd/MM HH:mm'),
          originalEnd: format(originalEndDate, 'EEE dd/MM HH:mm'),
          finalStart: format(finalStartDate, 'EEE dd/MM HH:mm'),
          finalEnd: format(finalEndDate, 'EEE dd/MM HH:mm'),
          durationMinutes: originalDurationMinutes,
          title: shift.title,
          targetWeek: format(currentWeekStart, 'dd/MM - ') + format(addDays(currentWeekStart, 6), 'dd/MM')
        });
        
        // Clean the shift data for database insertion
        return {
          title: shift.title,
          description: shift.description || '',
          start_date: toLocalISOString(finalStartDate),
          end_date: toLocalISOString(finalEndDate),
          location: shift.location || '',
          color: shift.color || 'Primary',
          team_id: teamId,
          created_by: userId,
          assigned_guards: []
        };
      });

      console.log('FINAL: Applying template with', shiftsToCreate.length, 'shifts ONLY to target week:', format(currentWeekStart, 'dd/MM'));

      // Insert shifts into team_schedules table - this is the ONLY place shifts are created
      const { data: insertedShifts, error } = await supabase
        .from('team_schedules')
        .insert(shiftsToCreate)
        .select();

      if (error) {
        console.error('Error inserting template shifts:', error);
        throw error;
      }

      // Logging handled automatically by database trigger
      console.log('Template shifts created, logging handled by database trigger');

      // Mark this template as applied in this session
      setAppliedTemplateIds(prev => new Set([...prev, templateId]));

      console.log('SUCCESS: Template applied successfully to target week only');
      toast.success(t('templateApplied') || 'Template applied successfully');
      
      // IMPORTANT: Call onApplyTemplate with an empty array to just trigger a refresh
      // The actual shifts are already inserted in the database, so we just need to refresh the calendar
      onApplyTemplate([]);
      onClose();
    } catch (error: any) {
      console.error('Error applying template:', error);
      toast.error(t('errorApplyingTemplate') || 'Error applying template');
    } finally {
      setIsApplying(false);
    }
  };

  const handleDeleteClick = (templateId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedTemplateId(templateId);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (selectedTemplateId) {
      await deleteTemplate(selectedTemplateId);
      setDeleteConfirmOpen(false);
    }
  };

  const handleSaveAsTemplate = () => {
    if (currentWeekEvents && currentWeekEvents.length > 0) {
      setSaveTemplateOpen(true);
    } else {
      toast.error(t('noEventsToSave') || 'No events to save as template');
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[550px]" aria-describedby="templates-dialog-description">
          <DialogHeader>
            <DialogTitle>{t("shiftTemplates") || "Shift Templates"}</DialogTitle>
            <DialogDescription id="templates-dialog-description">
              {t("templatesDescription") || "Apply saved templates to quickly add shifts to your schedule"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex justify-end mb-4">
            <Button 
              variant="outline" 
              onClick={handleSaveAsTemplate}
              className="flex items-center gap-1"
            >
              <PlusCircle className="h-4 w-4" />
              {t("saveAsTemplate") || "Save Current as Template"}
            </Button>
          </div>
          
          <ScrollArea className="max-h-[400px] pr-4">
            {isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center space-x-4 p-4 border rounded-md">
                    <Skeleton className="h-12 w-12 rounded-full" />
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-[200px]" />
                      <Skeleton className="h-4 w-[150px]" />
                    </div>
                  </div>
                ))}
              </div>
            ) : templates.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-4 text-lg font-semibold">
                  {t("noTemplatesYet") || "No templates yet"}
                </h3>
                <p className="text-sm text-muted-foreground mt-2">
                  {t("noTemplatesDescription") || "Templates you save will appear here"}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {templates.map((template) => (
                  <div 
                    key={template.id}
                    className="flex justify-between items-start p-4 border rounded-md hover:bg-accent/50 cursor-pointer transition-colors"
                    onClick={() => handleApplyTemplate(template.id)}
                  >
                    <div className="flex-1">
                      <div className="flex items-center">
                        <h4 className="font-medium text-base">{template.name}</h4>
                        <ChevronRight className="ml-2 h-4 w-4 text-muted-foreground" />
                        {appliedTemplateIds.has(template.id) && (
                          <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                            Applied
                          </span>
                        )}
                      </div>
                      {template.description && (
                        <p className="text-sm text-muted-foreground mt-1">{template.description}</p>
                      )}
                      <div className="flex items-center mt-2 text-xs text-muted-foreground">
                        <Calendar className="h-3 w-3 mr-1" />
                        <span>
                          {format(new Date(template.created_at), 'PPP')}
                        </span>
                        <span className="mx-2">•</span>
                        <span>
                          {template.schedule_data?.shifts?.length || 0} {t("shifts") || "shifts"}
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive/90 hover:bg-destructive/10"
                      onClick={(e) => handleDeleteClick(template.id, e)}
                      disabled={isApplying}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
          
          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={onClose} disabled={isApplying}>
              {t("close") || "Close"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteTemplate") || "Delete Template"}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("deleteTemplateConfirmation") || "Are you sure you want to delete this template? This action cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel") || "Cancel"}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("delete") || "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SaveTemplateDialog 
        isOpen={saveTemplateOpen}
        onClose={() => setSaveTemplateOpen(false)}
        teamId={teamId}
        events={currentWeekEvents}
        onSaved={() => {
          setSaveTemplateOpen(false);
          fetchTemplates();
        }}
      />
    </>
  );
};

export default TemplatesDialog;
